/**
 * Risk Matrix Editor Component
 *
 * Interactive 4x4 grid editor for Risk Matrix (Impact × Feasibility → Risk Value 1-5)
 */

import React from 'react';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import InstructionPanel from '../../../components/ui/InstructionPanel/InstructionPanel';
import { getRiskValueColor } from '../../../utils/severityStyle';
import styles from './MatrixEditor.module.css';

const IMPACT_LEVELS = ['Severe', 'Major', 'Moderate', 'Negligible'];
const FEASIBILITY_LEVELS = ['High', 'Medium', 'Low', 'Very Low'];

export default function RiskMatrixEditor() {
  const { config, updateConfiguration } = useConfiguration();

  if (!config?.configuration?.risk_matrix) {
    return <div className={styles.noData}>No risk matrix configuration available</div>;
  }

  const riskMatrix = config.configuration.risk_matrix;

  const handleCellChange = (impact, feasibility, newValue) => {
    const key = `${impact},${feasibility}`;
    const value = parseInt(newValue, 10);

    if (isNaN(value) || value < 1 || value > 5) {
      return;
    }

    const updatedMatrix = {
      ...riskMatrix,
      values: {
        ...riskMatrix.values,
        [key]: value
      }
    };

    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        risk_matrix: updatedMatrix
      }
    };

    updateConfiguration(updatedConfig);
  };

  const getRiskValue = (impact, feasibility) => {
    const key = `${impact},${feasibility}`;
    return riskMatrix.values[key] || 1;
  };

  const getRiskColor = (value) => getRiskValueColor(value) ?? 'var(--color-status-success)';

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Risk Matrix Configuration</h2>
      </div>

      <div className={styles.matrixContainer}>
        <div className={styles.matrixWrapper}>
          {/* Matrix grid with Y-axis on left */}
          <div className={styles.matrixContent}>
            {/* Y-axis section (left side) */}
            <div className={styles.yAxisSection}>
              <div className={styles.yAxisLabel}>
                <span>Impact Level</span>
              </div>
              <div className={styles.yAxis}>
                {IMPACT_LEVELS.map((impact) => (
                  <div key={impact} className={styles.axisLabel}>
                    {impact}
                  </div>
                ))}
              </div>
            </div>

            {/* Grid cells */}
            <div className={styles.gridContainer}>
              <div className={styles.grid}>
                {IMPACT_LEVELS.map((impact) => (
                  <div key={impact} className={styles.gridRow}>
                    {FEASIBILITY_LEVELS.map((feasibility) => {
                      const value = getRiskValue(impact, feasibility);
                      const color = getRiskColor(value);

                      return (
                        <div
                          key={`${impact}-${feasibility}`}
                          className={styles.gridCell}
                          style={{ backgroundColor: color, borderColor: color }}
                        >
                          <select
                            value={value}
                            onChange={(e) => handleCellChange(impact, feasibility, e.target.value)}
                            className={styles.cellSelect}
                          >
                            <option value={1}>1 - Very Low</option>
                            <option value={2}>2 - Low</option>
                            <option value={3}>3 - Medium</option>
                            <option value={4}>4 - High</option>
                            <option value={5}>5 - Critical</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* X-axis labels */}
              <div className={styles.xAxis}>
                {FEASIBILITY_LEVELS.map((feasibility) => (
                  <div key={feasibility} className={styles.axisLabel}>
                    {feasibility}
                  </div>
                ))}
              </div>

              {/* X-axis label */}
              <div className={styles.xAxisLabel}>
                <span>Attack Feasibility</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className={styles.legend}>
        <h3>Risk Level Legend</h3>
        <div className={styles.legendItems}>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: getRiskColor(5) }}></div>
            <span>5 - Critical Risk</span>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: getRiskColor(4) }}></div>
            <span>4 - High Risk</span>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: getRiskColor(3) }}></div>
            <span>3 - Medium Risk</span>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: getRiskColor(2) }}></div>
            <span>2 - Low Risk</span>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: getRiskColor(1) }}></div>
            <span>1 - Very Low Risk</span>
          </div>
        </div>
      </div>

      {/* Help text */}
      <InstructionPanel title="How to use">
        <ul>
          <li>Each cell represents a combination of Impact (vertical) and Attack Feasibility (horizontal)</li>
          <li>Select a risk value (1-5) for each combination</li>
          <li>Higher impact + higher feasibility should typically result in higher risk values</li>
          <li><strong>ISO 21434 defaults:</strong> Severe+High=5, Severe+Medium=4, Major+High=4, etc.</li>
          <li>Changes are saved when you click "Save Changes" in the header</li>
        </ul>
      </InstructionPanel>
    </div>
  );
}
